import React, { useContext, useState, useEffect } from 'react';
import { Search, AlertCircle, Loader2, Eye, Filter, X, ChevronDown, ListChecks, Building2, User, Calendar, History } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../../../context/AuthContext';
import superAdminAuditService from '../../../../../services/audit_services/superAdminAuditService';
import Pagination from '../../../../../components/Pagination';

export default function AllAuditSectionsList() {
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);

  const [showFiltersSection, setShowFiltersSection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sections, setSections] = useState([]);
  
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 25,
    total_pages: 0
  });

  const [filters, setFilters] = useState({
    search: '',
    organization: '',
    sortBy: 'newest'
  });

  useEffect(() => {
    fetchSections();
  }, [pagination.page, pagination.page_size]);

  const fetchSections = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pagination.page,
        page_size: pagination.page_size,
      };

      if (filters.search) params.search = filters.search;
      if (filters.organization) params.organization = filters.organization;
      if (filters.sortBy) params.ordering = mapSortToOrdering(filters.sortBy);

      const response = await superAdminAuditService.getAllAuditSections(authTokens.access, params);

      setSections(response.results || []);
      setPagination(prev => ({
        ...prev,
        count: response.count,
        total_pages: response.total_pages
      }));
    } catch (err) {
      setError(err.message);
      setSections([]);
    } finally {
      setLoading(false);
    }
  };

  const mapSortToOrdering = (sortBy) => {
    const sortMap = {
      'name': 'section_name',
      'organization': 'organization__organisation_name',
      'newest': '-created_at',
      'oldest': 'created_at'
    };
    return sortMap[sortBy] || '-created_at';
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchSections();
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      organization: '',
      sortBy: 'newest'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => fetchSections(), 0);
  };

  const handleShowFilters = () => {
    setShowFiltersSection(!showFiltersSection);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ 
      ...prev, 
      page_size: newPageSize, 
      page: 1
    }));
  };

  const handleViewDetails = (sectionId) => {
    navigate(`/admin-panel/audit-sections/${sectionId}`);
  };

  const handleViewHistory = (sectionId) => {
    navigate(`/admin-panel/audit-sections/${sectionId}/history`);
  };

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Sections d'audit</h2>
          <p className="text-xs text-gray-500 mt-1">Gérez toutes les sections d'audit du système</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/admin-panel/audit-sections/history')}
            className="inline-flex items-center px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <History className="h-4 w-4 mr-2" />
            Voir tout l'historique
          </button>
          
          <button
            onClick={handleShowFilters}
            className="inline-flex items-center px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtres
            <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${showFiltersSection ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {showFiltersSection && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Rechercher</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Nom de section..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Organisation</label>
              <input
                type="text"
                placeholder="Nom de l'organisation"
                value={filters.organization}
                onChange={(e) => handleFilterChange('organization', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Trier par</label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="newest">Plus récent</option>
                <option value="oldest">Plus ancien</option>
                <option value="name">Nom (A-Z)</option>
                <option value="organization">Organisation (A-Z)</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 mt-4">
            <button
              onClick={handleClearFilters}
              className="inline-flex items-center px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
            >
              <X className="h-4 w-4 mr-1" />
              Effacer
            </button>
            <button
              onClick={handleApplyFilters}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Appliquer les filtres
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Section</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Plan d'audit</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Organisation</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Questions</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Créé par</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Date de création</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
                      <span className="ml-2 text-sm text-gray-600">Chargement...</span>
                    </div>
                  </td>
                </tr>
              ) : sections.length > 0 ? (
                sections.map((section) => (
                  <tr key={section.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-gradient-to-r from-black to-gray-900 rounded-full flex items-center justify-center mr-3">
                          <ListChecks className="h-4 w-4 text-green-500" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-medium text-gray-900">{section.section_name}</p>
                          {section.description && (
                            <p className="text-xs text-gray-500 truncate max-w-xs">{section.description}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-900">
                        {section.audit_plan?.plan_name || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <Building2 className="h-3 w-3 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-900">
                          {section.organization?.name || 'Aucune organisation'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">
                        {section.questions_count || 0} question(s)
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <User className="h-3 w-3 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-900">
                          {section.created_by?.name || section.created_by?.email || 'Inconnu'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-600">
                          {new Date(section.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleViewDetails(section.id)}
                          className="inline-flex items-center justify-center p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleViewHistory(section.id)}
                          className="inline-flex items-center justify-center p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                          title="Voir l'historique"
                        >
                          <History className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center">
                      <ListChecks className="h-12 w-12 text-gray-300 mb-3" />
                      <p className="text-xs text-gray-500">Aucune section d'audit trouvée</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          totalItems={pagination.count}
          pageSize={pagination.page_size}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          loading={loading}
          itemName="sections"
        />
      </div>
    </div>
  );
}
